import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  itemsApi, purchaseInvoicesApi, vendorsApi, warehousesApi,
  type ItemDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface DraftLine {
  key: string;
  item: ItemDto | null;
  batchNo: string;
  expiryDate: string;
  qty: number;
  rate: number;
  taxAmount: number;
  discountAmount: number;
}
const blank = (): DraftLine => ({
  key: crypto.randomUUID(),
  item: null, batchNo: "", expiryDate: "",
  qty: 0, rate: 0, taxAmount: 0, discountAmount: 0,
});

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

/**
 * Direct Purchase Invoice — bill-entry desk only. Save = approve + receive stock + post JE
 * in one shot. The DPI- numbering series distinguishes these from the normal PI- flow.
 * Posted is terminal — corrections via reversal/debit note only.
 */
export function CreateDirectPurchaseInvoicePage() {
  const navigate = useNavigate();
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().slice(0, 10));
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState("");
  const [vendorInvoiceDate, setVendorInvoiceDate] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);

  const vendors = useQuery({
    queryKey: ["vendors", "active"],
    queryFn: () => vendorsApi.list({ pageSize: 200, isActive: true }),
  });
  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });
  const items = useQuery({
    queryKey: ["items", "picker", search],
    queryFn: () => itemsApi.list({ search: search || undefined, pageSize: 50 }),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      // Changing the item refills the rate from its default MRP (even if a rate was already set).
      if ("item" in patch) next.rate = patch.item?.defaultMrp ?? 0;
      // Auto-calculate GST from the item's tax rate whenever item / qty / rate change.
      if ("item" in patch || "qty" in patch || "rate" in patch)
        next.taxAmount = Math.round(next.qty * next.rate * (next.item?.gstRate ?? 0)) / 100;
      return next;
    }));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const totalAmount = subTotal + taxTotal - discountAmount;

  const valid = vendorId && warehouseId &&
    lines.every(l => l.item && l.batchNo.trim() && l.qty > 0 && l.rate >= 0);

  const mutation = useMutation({
    meta: { successMessage: "Direct purchase invoice created" },
    mutationFn: () => purchaseInvoicesApi.createDirect({
      vendorId,
      warehouseId,
      documentDate: new Date(documentDate).toISOString(),
      vendorInvoiceNo: vendorInvoiceNo || undefined,
      vendorInvoiceDate: vendorInvoiceDate
        ? new Date(vendorInvoiceDate).toISOString()
        : undefined,
      discountAmount,
      notes: notes || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id,
        batchNo: l.batchNo.trim(),
        expiryDate: l.expiryDate || undefined,
        qty: l.qty, rate: l.rate,
        taxAmount: l.taxAmount, discountAmount: l.discountAmount,
      })),
    }),
    onSuccess: (r) => navigate(`/procurement/purchase-invoices/${r.id}`),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>New Direct Purchase Invoice</Typography>
        <Chip size="small" color="warning" variant="outlined" label="Direct" />
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <b>Save will post immediately.</b> The system creates the PI with a <b>DPI-</b> number,
        receives stock into the chosen warehouse, and posts the GL journal (DR Inventory / DR GST / CR AP)
        in one operation. Posted is terminal — corrections require a reversal/debit note.
      </Alert>

      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Vendor *" sx={{ minWidth: 240 }}
            value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <MenuItem value="">— pick vendor —</MenuItem>
            {vendors.data?.items.map(v => (
              <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Receiving warehouse *" sx={{ minWidth: 240 }}
            value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">— pick warehouse —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="PI date *" size="small"
            InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField label="Vendor invoice #" size="small" sx={{ minWidth: 200 }}
            value={vendorInvoiceNo} onChange={(e) => setVendorInvoiceNo(e.target.value)} />
          <TextField type="date" label="Vendor invoice date" size="small"
            InputLabelProps={{ shrink: true }}
            value={vendorInvoiceDate} onChange={(e) => setVendorInvoiceDate(e.target.value)} />
          <TextField type="number" label="Header discount" size="small" sx={{ width: 160 }}
            value={discountAmount} onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
            inputProps={{ min: 0, step: "0.01" }} />
        </Stack>

        <TextField label="Notes" fullWidth multiline minRows={2} size="small"
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines (stock items only)</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])}>Add line</Button>
        </Stack>

        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
            <TableCell sx={{ width: 140 }}>Batch # *</TableCell>
            <TableCell sx={{ width: 150 }}>Expiry</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Qty *</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Rate *</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Tax</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Disc</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>Line total</TableCell>
            <TableCell sx={{ width: 60 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete size="small"
                    options={items.data?.items ?? []}
                    getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                    value={l.item}
                    onChange={(_, v) => update(l.key, { item: v })}
                    onInputChange={(_, v) => setSearch(v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(p) => <TextField {...p} placeholder={`Line ${idx + 1}`} />}
                    loading={items.isLoading}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.batchNo}
                    onChange={(e) => update(l.key, { batchNo: e.target.value })} />
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={l.expiryDate}
                    onChange={(e) => update(l.key, { expiryDate: e.target.value })} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.qty}
                    onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 100 }}
                    value={l.rate}
                    onChange={(e) => update(l.key, { rate: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.taxAmount}
                    onChange={(e) => update(l.key, { taxAmount: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.discountAmount}
                    onChange={(e) => update(l.key, { discountAmount: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  {inr.format(l.qty * l.rate + l.taxAmount - l.discountAmount)}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="flex-end" spacing={4} sx={{ mt: 3 }}>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Subtotal</Typography>
            <Typography>{inr.format(subTotal)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Tax</Typography>
            <Typography>{inr.format(taxTotal)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Discount</Typography>
            <Typography>-{inr.format(discountAmount)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6">{inr.format(totalAmount)}</Typography>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/procurement/purchase-invoices")}>Cancel</Button>
          <Button variant="contained" color="warning"
            disabled={!valid || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Save &amp; Post
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
